import { IAllowedActionScope, IChangeDescriptionActionScope, IChangeOwnerActionScope, IChangeTagsActionScope, IDeleteActionScope, IGetUserUrlScope, RefreshContentScope } from '../../app/ActionPlaceScopes';
import { getAppStore } from '../../app/AppStore';
import { IPlaceScope } from '../../app/IPlaceScope';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { DatasourceSpaceOwnProps } from '../../app/spaces/DatasourceSpace/DatasourceSpace';
import { DetailsDialogInAngularPlaceHelper, IDetailsDialogInAngularPlaceHelper } from '../../app/spaces/DetailsDialog/AngularContentDetailsDialog';
import { Location } from '../../common/react/Location';
import { RevisionHistoryAction } from '../../revisionHistory/services/RevisionHistoryAction';
import { FeatureFlags } from '../FeatureFlags';
import { ActionContext } from '../helpers/ActionContext';
import { RemoteQueryAgentHelper } from '../helpers/RemoteQueryAgentHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { IDataSourceDetailInfo, IDataSourceInfo } from '../models/IDataSourceInfo';
import { IUserInfo } from '../models/IUserInfo';
import { IItemStatisticsInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { IDatasourceActions } from '../ServerApi/types/IDatasourceActions';
import { IDatasourceUsageInfo } from '../ServerApi/types/IUsageInfo';
import { ChangeDatasourceDescriptionAction } from '../services/ChangeDatasourceDescriptionAction';
import { ContentActions, ContentActionsService } from '../../commonInfra/contentActions/ContentActions';
import { DeleteContentsAction } from '../services/DeleteContentsAction';
import { ModalService } from '../services/ModalService';
import { ToasterService } from '../services/ToasterService';

// Scope and props needed to display the details dialog
export interface IDatasourceDetailsDialogScope extends
  IAllowedActionScope<IDatasourceActions>,
  IChangeDescriptionActionScope,
  IChangeOwnerActionScope,
  IChangeTagsActionScope,
  IDeleteActionScope,
  IPlaceScope,
  RefreshContentScope,
  IGetUserUrlScope
{
  datasource: IDataSourceDetailInfo & IItemStatisticsInfo<IDatasourceUsageInfo>;
  datasourceSet: Set<IDataSourceDetailInfo>;
  showRemoteQueryAgent: boolean;
  moveToProject: ($event: JQueryEventObject) => void;
  showRevisionHistory: ($event: JQueryEventObject) => any;
  changeRemoteQueryAgent: ($event: JQueryEventObject) => any;
  showDatasourceCertification: boolean;
  certificationStatusUpdated: ($event: JQueryEventObject) => void;
  currentRemoteQueryAgentDisplayName: () => string;
  datasourceSpaceProps: DatasourceSpaceOwnProps;

  onNlpSettingSaved: () => void;
  getProjectUrl: (projectId: string) => string;
}

export class DatasourceDetailsDialogCtrl {
  private ContentActions: ContentActions;
  public static $inject = [
    '$q',
    '$scope',
    'ChangeDatasourceDescriptionAction',
    'DeleteContentsAction',
    'datasourceActions',
    'datasourceDetailInfo',
    'datasourceInfo',
    'RevisionHistoryAction',
    'ModalService',
    'detailsDialogHelper',
  ];

  constructor(
            $q: ng.IQService,
    private $scope: IDatasourceDetailsDialogScope,
    private ChangeDatasourceDescriptionAction: ChangeDatasourceDescriptionAction,
    private DeleteContentsAction: DeleteContentsAction,
            datasourceActions: IDatasourceActions,
            datasourceDetailInfo: IDataSourceDetailInfo,
    private datasourceInfo: IDataSourceInfo,
    private RevisionHistoryAction: RevisionHistoryAction,
            ModalService: ModalService,
            detailsDialogHelper: DetailsDialogInAngularPlaceHelper
  ) {
    this.ContentActions = ContentActionsService;
    const allowedActionFn = (actionName: keyof IDatasourceActions) => (!!datasourceActions && datasourceActions[actionName]);
    const refreshContentFn = () => window.location.reload();

    this.loadDatasourceIntoScope(datasourceDetailInfo);
    this.initScopeForDetailsPane($scope, $q, allowedActionFn, refreshContentFn, ModalService, detailsDialogHelper, ToasterService.instance);
  }

  private loadDatasourceIntoScope(newDatasourceDetailInfo: IDataSourceDetailInfo) {
    const datasourceWithUsageInfo = {
      usageInfo: this.datasourceInfo.usageInfo,
      usageInfoMaxValues: this.datasourceInfo.usageInfoMaxValues,
      ...newDatasourceDetailInfo
    };

    this.$scope.datasource = datasourceWithUsageInfo;
    this.$scope.datasourceSet = Set.singletonForIdItem(newDatasourceDetailInfo);

    this.$scope.objectName = newDatasourceDetailInfo.name;
    this.$scope.ownerId = newDatasourceDetailInfo.owner.id;
  }

  private initScopeForDetailsPane(
      $scope: IDatasourceDetailsDialogScope,
      $q: ng.IQService,
      allowedActionFn: (actionName: string) => boolean,
      refreshContentFn: () => void,
      modalService: ModalService,
      dialogHelper: IDetailsDialogInAngularPlaceHelper,
      toaster: ToasterService
  ) {

    // $scope.refreshContent is used by some of the menu actions too; be very careful about deleting it
    $scope.refreshContent = refreshContentFn;
    $scope.allowedAction = allowedActionFn;

    $scope.del = ($event: JQueryEventObject) => {
      this.DeleteContentsAction.confirmDeleteContents([$scope.datasource], ActionContext.fromEvent($event))
      .then((result) => {
        toaster.markToastsAsDoNotDismissOnLocationChange();
        const siteUrls = siteUrlsSelector(getAppStore().getState());
        Location.instance.setUrl(siteUrls.projectUrl($scope.datasource.project.id));
      });
    };

    $scope.changeDescription = (newDescription: string) => {
      return this.ChangeDatasourceDescriptionAction.execute($scope.datasource, newDescription)
        .then(dialogHelper.inlineEditResultHandler);
    };

    $scope.changeOwner = ($event: JQueryEventObject) => {
      const actionContext = ActionContext.fromEvent($event);
      return this.ContentActions.changeOwnerOnDatasources($scope.datasourceSet, actionContext)
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    $scope.showRevisionHistory = () => {
      return this.RevisionHistoryAction.showDataSourceRevisionHistory($scope.datasource)
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    $scope.moveToProject = ($event: JQueryEventObject) => {
      const actionContext = ActionContext.fromEvent($event);
      return this.ContentActions.moveDatasourcesToProject($scope.datasourceSet, actionContext)
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    // Allow direct assignment of a remote query agent to a datasource
    $scope.showRemoteQueryAgent = FeatureFlags.bridgeLiveDirectAssignEnabled &&
      !$scope.datasource.hasExtracts &&
      !$scope.datasource.testingConnectionAllowed;

    $scope.currentRemoteQueryAgentDisplayName = () => {
      return RemoteQueryAgentHelper.getRemoteQueryAgentDisplayName($scope.datasource);
    };

    $scope.changeRemoteQueryAgent = () => {
      return this.ContentActions.changeRemoteQueryAgentOnDatasource($scope.datasource)
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    $scope.showDatasourceCertification = FeatureFlags.datasourceCertificationEnabled;

    $scope.certificationStatusUpdated = () => {
      const fakeResult: null = null; // The handler needs a result, but it ignores it if not present
      dialogHelper.inlineEditResultHandler(fakeResult);
    };

    $scope.changeTags = (newTagsList: string[]) => {
      return this.ContentActions.setTagsOnDatasource($scope.datasource, newTagsList)
        .then(ResultHelper.rejectResultOnError($q))
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    $scope.onNlpSettingSaved = () => {
      dialogHelper.actionFromSeparateDialogResultHandler();
      dismissDialog();
    };

    $scope.getProjectUrl = (projectId: string): string => {
      const siteUrls = siteUrlsSelector(getAppStore().getState());
      return siteUrls.projectUrl(projectId);
    };

    $scope.getUserUrl = (user: IUserInfo): string => {
      const siteUrls = siteUrlsSelector(getAppStore().getState());
      return siteUrls.userUrl(user.username, user.domainName);
    };

    function dismissDialog() {
      modalService.dismissAll();
    }
  }
}

Module.instance().controller('DatasourceDetailsDialogCtrl', DatasourceDetailsDialogCtrl);
